/**
 * Input handling. Combines keyboard (A/←, D/→, Space, Esc, R) and
 * pointer/touch (left half / right half) into a single `SteerDir` output.
 *
 * Auto-detects which mode is active: if the keyboard is used, it takes
 * priority and pointer input is ignored for the duration of the keypress;
 * if the pointer is used first, keyboard is ignored until released.
 */
import { SteerDir } from './engine/types';

export interface InputState {
  steer: SteerDir;
  pausePressed: boolean;
  resetPressed: boolean;
  cheatPressed: boolean;
  quitPressed: boolean;
}

export type InputListener = (state: InputState) => void;

const KEY_LEFT = new Set(['a', 'A', 'ArrowLeft']);
const KEY_RIGHT = new Set(['d', 'D', 'ArrowRight']);
const KEY_PAUSE = new Set([' ', 'p', 'P', 'Escape']);
const KEY_RESET = new Set(['r', 'R']);
const KEY_QUIT = new Set(['q', 'Q']);
const KEY_CHEAT = new Set(['i', 'I']);

export class InputManager {
  private listeners: InputListener[] = [];
  private keyLeft = false;
  private keyRight = false;
  private keyboardActive = false;
  private oneShot = { pause: false, reset: false, cheat: false, quit: false };
  private fullScreen = false;
  private isPointerDown = false;

  private pointerHandler = (e: PointerEvent) => this.onPointer(e);
  private keyDownHandler = (e: KeyboardEvent) => this.onKeyDown(e);
  private keyUpHandler = (e: KeyboardEvent) => this.onKeyUp(e);
  private blurHandler = () => this.clearAll();

  attach(target: HTMLElement, options?: { fullScreen?: boolean }): void {
    this.fullScreen = options?.fullScreen ?? false;
    target.addEventListener('pointerdown', this.pointerHandler);
    target.addEventListener('pointermove', this.pointerHandler);
    target.addEventListener('pointerup', this.pointerHandler);
    target.addEventListener('pointercancel', this.pointerHandler);
    target.addEventListener('pointerleave', this.pointerHandler);
    window.addEventListener('keydown', this.keyDownHandler);
    window.addEventListener('keyup', this.keyUpHandler);
    window.addEventListener('blur', this.blurHandler);
  }

  detach(target: HTMLElement): void {
    target.removeEventListener('pointerdown', this.pointerHandler);
    target.removeEventListener('pointermove', this.pointerHandler);
    target.removeEventListener('pointerup', this.pointerHandler);
    target.removeEventListener('pointercancel', this.pointerHandler);
    target.removeEventListener('pointerleave', this.pointerHandler);
    window.removeEventListener('keydown', this.keyDownHandler);
    window.removeEventListener('keyup', this.keyUpHandler);
    window.removeEventListener('blur', this.blurHandler);
  }

  subscribe(fn: InputListener): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private emit(): void {
    const effectiveDir = this.keyboardActive
      ? this.keyLeft
        ? SteerDir.LEFT
        : this.keyRight
          ? SteerDir.RIGHT
          : SteerDir.NONE
      : this.pointerDir;
    const state: InputState = {
      steer: effectiveDir,
      pausePressed: this.oneShot.pause,
      resetPressed: this.oneShot.reset,
      cheatPressed: this.oneShot.cheat,
      quitPressed: this.oneShot.quit,
    };
    this.oneShot.pause = false;
    this.oneShot.reset = false;
    this.oneShot.cheat = false;
    this.oneShot.quit = false;
    for (const fn of this.listeners) fn(state);
  }

  private pointerDir: SteerDir = SteerDir.NONE;

  private onPointer(e: PointerEvent): void {
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    if (e.type === 'pointerdown') {
      this.isPointerDown = true;
    } else if (e.type === 'pointerup' || e.type === 'pointercancel') {
      this.isPointerDown = false;
      this.pointerDir = SteerDir.NONE;
      this.emit();
      return;
    } else if (e.type === 'pointerleave') {
      // Don't zero the direction — just flag that we're no longer tracking.
      // The pointer might re-enter without a new pointerdown, and we don't
      // want a sudden stop-and-start that feels like a "fling."
      this.isPointerDown = false;
      return;
    } else if (e.type === 'pointermove') {
      // While no pointer is held, ignore pointermove (mouse hover).
      if (!this.isPointerDown) return;
    }

    const mid = this.fullScreen ? window.innerWidth / 2 : rect.left + rect.width / 2;
    this.pointerDir = e.clientX < mid ? SteerDir.LEFT : SteerDir.RIGHT;
    this.emit();
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.repeat) return;
    if (KEY_LEFT.has(e.key)) {
      this.keyLeft = true;
      this.keyboardActive = true;
    } else if (KEY_RIGHT.has(e.key)) {
      this.keyRight = true;
      this.keyboardActive = true;
    } else if (KEY_PAUSE.has(e.key)) {
      this.oneShot.pause = true;
      e.preventDefault();
    } else if (KEY_RESET.has(e.key)) {
      this.oneShot.reset = true;
    } else if (KEY_QUIT.has(e.key)) {
      this.oneShot.quit = true;
    } else if (KEY_CHEAT.has(e.key)) {
      this.oneShot.cheat = true;
    } else {
      return;
    }
    this.emit();
  }

  private onKeyUp(e: KeyboardEvent): void {
    if (KEY_LEFT.has(e.key)) {
      this.keyLeft = false;
      this.keyboardActive = this.keyRight;
    } else if (KEY_RIGHT.has(e.key)) {
      this.keyRight = false;
      this.keyboardActive = this.keyLeft;
    }
    this.emit();
  }

  private clearAll(): void {
    this.keyLeft = false;
    this.keyRight = false;
    this.keyboardActive = false;
    this.isPointerDown = false;
    this.pointerDir = SteerDir.NONE;
    this.emit();
  }
}
